import { Button } from '@creditornot/cb-button';
import { Column, DataTable } from '@creditornot/cb-data-table';
import { Download } from '@creditornot/cb-icons';
import { Empty } from '@creditornot/cb-placeholders';
import { LegacyPopover } from '@creditornot/cb-popover';
import { Spinner } from '@creditornot/cb-progress';
import { Tag, TagVariant } from '@creditornot/cb-tag';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import React, { FC, useState } from 'react';
import toast from 'react-hot-toast';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { getDac7DownloadLink } from 'apis/merchant-gateway/insights/dac7reports/api';
import { getAllMerchantPayouts, getCombinedMerchantPayoutReport } from 'apis/merchant-payout-service/api';
import { AllMerchantPayouts, MerchantPayout, MerchantPayoutStatus } from 'apis/merchant-payout-service/types';
import PageHeader from 'common/components/PageHeader';
import MonetaryAmount from 'common/formatting/MonetaryAmount';
import { convertMonetaryIntToDecimal } from 'common/utils/currencyDecimals';
import { downloadFileFromAxiosResponse, downloadFromUrl } from 'common/utils/download';
import { countryToTimezone } from 'locale';
import { useIsDac7ReportEnabled } from 'modules/merchant/hooks/useIsDac7ReportEnabled';

import { TaxReportingAlert } from './TaxReportingAlert';
import { TaxReportingInfoModal } from './TaxReportingInfoModal';
import { useMerchantContext } from '../merchant/MerchantContext';

const EmptyMessage = styled(Empty)`
  margin-block-start: 100px;
`;

const DownloadIcon = styled(Download)`
  width: 16px;
  cursor: pointer;
`;

const DisabledDownloadIcon = styled(Download)`
  width: 16px;
  opacity: 0.5;
  pointer-events: none;
`;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
`;

const LoadingSpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  padding-block-start: 40px;
  padding-block-end: 40px;
`;

const AlertContainer = styled.div`
  margin: 16px;
`;

const Loading = () => (
  <LoadingSpinnerContainer>
    <Spinner size="large" />
  </LoadingSpinnerContainer>
);

const intervalTranslations: { [key: string]: string } = {
  custom: 'payout-reports.custom',
  hexa_monthly: 'payout-reports.hexa-monthly',
  monthly: 'payout-reports.monthly',
  semi_monthly: 'payout-reports.semi-monthly',
  semi_weekly: 'payout-reports.semi-weekly',
  weekly: 'payout-reports.weekly',
};

const payoutStatuses: { [key in MerchantPayoutStatus]: string } = {
  RUNNING: 'campaigns.status.running',
  SUCCESS: 'menu.bulk-image.status-success',
  FAILED: 'menu.bulk-image.status-failure',
  NO_VENUES: 'menu.menu-sync.empty-venue-list',
};

const payoutStatusTagVariants: { [key in MerchantPayoutStatus]: TagVariant } = {
  RUNNING: 'lightYellow',
  SUCCESS: 'lightBlue',
  FAILED: 'lightRed',
  NO_VENUES: 'lightBlue',
};

const MerchantPayouts: FC = () => {
  const intl = useIntl();
  const {
    merchant: {
      country: merchantCountry,
      itemid: { $oid: merchantId },
    },
  } = useMerchantContext();
  const isDac7Enabled = useIsDac7ReportEnabled({ merchantId, merchantCountry });

  const { data: dac7ReportLink } = useQuery(
    ['getDac7DownloadLink', merchantId],
    () => getDac7DownloadLink(merchantId),
    { enabled: isDac7Enabled },
  );

  const { data: merchantPayoutsData, isLoading } = useQuery<AllMerchantPayouts>(
    ['merchantPayoutsData', merchantId],
    () => getAllMerchantPayouts(merchantId),
  );

  const [payoutsBeingDownloaded, setPayoutsBeingDownloaded] = useState<string[]>([]);
  const [showMoreInfo, setShowMoreInfo] = React.useState(false);

  const t = (id: string) => intl.formatMessage({ id });

  const isPayoutForHRVBefore2023 = (country: string, year: number) => country === 'HRV' && year < 2023;
  const downloadReport = (payout: MerchantPayout) => {
    setPayoutsBeingDownloaded(_ => [..._, payout.id]);

    getCombinedMerchantPayoutReport(payout.merchantId, payout.id)
      .then(downloadFileFromAxiosResponse)
      .catch(() => toast('Download failed'))
      .finally(() => setPayoutsBeingDownloaded(current => current.filter(_ => _ !== payout.id)));
  };

  if (isLoading || !merchantPayoutsData) {
    return <Loading />;
  }

  const columns: Column<MerchantPayout, Record<string, unknown>>[] = [
    {
      id: '1',
      name: t('payout-reports.report'),
      render: ({ record: payout }) => (
        <>
          {t('payout-reports.report')} {t(intervalTranslations[payout.interval.type])} {payout.interval.number}
        </>
      ),
    },
    {
      id: '2',
      name: t('special-hours.period'),
      render: ({ record: payout }) => {
        const timezone = countryToTimezone(payout.country);
        const startDate = DateTime.fromISO(payout.interval.startDate).setZone(timezone);
        const endDate = DateTime.fromISO(payout.interval.endDate).setZone(timezone);
        return (
          <>
            {startDate.toFormat('MMM d, y')} - {endDate.toFormat('MMM d, y')}
          </>
        );
      },
    },
    {
      id: '3',
      name: t('payout-reports.amount'),
      render: ({ record: payout }) => {
        const timezone = countryToTimezone(payout.country);
        const endDateYear = DateTime.fromISO(payout.interval.endDate).setZone(timezone).year;

        return (
          <>
            {payout.status === MerchantPayoutStatus.RUNNING && (
              <Tag variant="lightYellow" uppercase>
                CALCULATING
              </Tag>
            )}
            {payout.status === MerchantPayoutStatus.FAILED && (
              <Tag variant="lightRed" uppercase>
                NOT_AVAILABLE
              </Tag>
            )}
            {payout.status === MerchantPayoutStatus.SUCCESS && (
              <MonetaryAmount
                amount={
                  isPayoutForHRVBefore2023(payout.country, endDateYear)
                    ? convertMonetaryIntToDecimal(payout.payoutAmount, 'HRK')
                    : convertMonetaryIntToDecimal(payout.payoutAmount, merchantPayoutsData.currency)
                }
                currency={isPayoutForHRVBefore2023(payout.country, endDateYear) ? 'HRK' : merchantPayoutsData.currency}
              />
            )}
          </>
        );
      },
    },
    {
      id: '4',
      name: t('payout-reports.bank_account'),
      render: () => (
        <LegacyPopover
          placement="right"
          renderArrow
          content={
            <div>
              {!!merchantPayoutsData.merchantBankAccount.length && t('payout-reports.bank_account_number_tooltip')}
            </div>
          }
        >
          {!!merchantPayoutsData.merchantBankAccount.length && merchantPayoutsData.merchantBankAccount}
        </LegacyPopover>
      ),
    },
    {
      id: '5',
      name: t('payout-reports.status'),
      render: ({ record: payout }) => (
        <Tag variant={payoutStatusTagVariants[payout.status]} uppercase>
          {t(payoutStatuses[payout.status])}
        </Tag>
      ),
    },
  ];

  if (merchantPayoutsData.isOptedInMerchantLevelArchiving) {
    columns.push({
      id: '6',
      name: '',
      alignContent: 'center',
      render: ({ record: payout }) => {
        const isPayoutBeingDownloaded = payoutsBeingDownloaded.includes(payout.id);
        const isDisabled =
          payout.payoutAmount === 0 || payout.status !== MerchantPayoutStatus.SUCCESS || !payout.archived;

        return (
          <>
            {isPayoutBeingDownloaded && <Spinner size="small" color="blue" />}
            {!isPayoutBeingDownloaded && isDisabled && <DisabledDownloadIcon />}
            {!isPayoutBeingDownloaded && !isDisabled && <DownloadIcon onClick={() => downloadReport(payout)} />}
          </>
        );
      },
    });
  }
  const handleModalClose = () => {
    setShowMoreInfo(false);
  };

  return (
    <Root>
      <PageHeader title={intl.formatMessage({ id: 'navigation.payout-reports' })}>
        {!!dac7ReportLink && (
          <Button size="small" variant="blue" onClick={() => downloadFromUrl(dac7ReportLink)}>
            {t('payout-reports.download-dac-7-2023-report')}
          </Button>
        )}
      </PageHeader>
      <AlertContainer>
        <TaxReportingAlert merchantId={merchantId} country={merchantCountry} isRoot={false} />
      </AlertContainer>
      {showMoreInfo && <TaxReportingInfoModal merchantId={merchantId} onClose={handleModalClose} />}
      {merchantPayoutsData.payouts.length === 0 && (
        <EmptyMessage title={intl.formatMessage({ id: 'purchases.messages.no-items' })} />
      )}
      {merchantPayoutsData.payouts.length !== 0 && (
        <DataTable<MerchantPayout>
          dataSource={merchantPayoutsData.payouts}
          columns={columns}
          getRowKey={payout => payout.id}
          loading={!merchantPayoutsData}
          emptyMessage={{
            title: t('state.error'),
            description: t('errors.unknown-error-retry-contact'),
          }}
          horizontalPadding="wide"
        />
      )}
    </Root>
  );
};

export default MerchantPayouts;
